import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Compass } from "lucide-react";
import { Layout } from "../components/Layout";
import { Skeleton } from "../components/ui/skeleton";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { useS, recommend } from "../lib/simple";
import { StartCard } from "../components/home/StartCard";
import { WorkCard } from "../components/home/WorkCard";
import { RecoCard } from "../components/home/RecoCard";
import { ToolsAccordion } from "../components/home/ToolsAccordion";
import { GoFurther } from "../components/starter/GoFurther";
import { NextCard } from "../components/home/NextCard";

const val = (r) => (r.status === "fulfilled" ? r.value.data : null);

function buildWork({ st, action, data, plan, items, s }) {
  const out = [];
  if (action && st.status && st.status !== "done") out.push({ key: "starter", title: action.title, sub: st.status === "draft" ? s("st_draft") : s("st_todo"), to: "/app/start" });
  const mission = (data.actions || []).filter((a) => a.isMission && !a.done).sort((a, b) => (a.day_offset ?? 99) - (b.day_offset ?? 99))[0];
  if (mission) out.push({ key: "mission", title: mission.title, sub: s("st_mission"), to: "/app/plan" });
  const pa = plan ? ["30", "60", "90"].flatMap((h) => plan.horizons?.[h] || []).find((a) => a.status === "progress" || a.status === "todo") : null;
  if (pa) out.push({ key: "diagplan", title: pa.title, sub: s("st_diag"), to: "/app/diagnostic" });
  const last = (items || []).find((d) => d.id !== st.draftId);
  if (last) out.push({ key: "content", title: last.title, sub: s("st_content"), to: "/app/content" });
  return out.slice(0, 3);
}

export default function Home() {
  const nav = useNavigate();
  const { data, setData } = useApp();
  const { s, lang } = useS();
  const [srv, setSrv] = useState(null);

  useEffect(() => {
    Promise.allSettled([api.get("/diagnostic"), api.get("/action-plan"), api.get("/deliverables")]).then((r) => {
      const d = val(r[0]); const p = val(r[1]);
      setSrv({ diag: d && d.diagnostic_id ? d : null, plan: p && p.plan_id ? p : null, items: val(r[2])?.items || [] });
    });
  }, []);

  if (!data || !data.scores) {
    return <Layout><div className="text-center py-20"><button onClick={() => nav("/onboarding")} className="text-neon min-h-[44px]" data-testid="go-onboarding">{s("start_title")} →</button></div></Layout>;
  }
  const st = data.starter || {};
  const update = (patch) => setData((d) => ({ ...d, starter: { ...(d?.starter || {}), ...patch, updatedAt: Date.now() } }));
  const action = st.answers ? recommend(st.answers, lang) : null;
  const work = srv ? buildWork({ st, action, data, plan: srv.plan, items: srv.items, s }) : [];
  const showStart = srv && !work.length && !st.dismissed && st.status !== "done";
  const priorities = srv?.diag?.priorities || [];

  return (
    <Layout>
      <div className="max-w-3xl space-y-4" data-testid="home-simple">
        <div>
          <p className="text-[hsl(var(--muted-foreground))] text-sm">{s("hello")}</p>
          <h1 className="font-display font-black text-2xl lg:text-3xl">{data.onboarding?.business_name || ""}</h1>
        </div>
        {!srv && <div className="lb-card p-6 space-y-3" data-testid="home-loading"><Skeleton className="h-6 w-2/3" /><Skeleton className="h-4 w-full" /><Skeleton className="h-12 w-full rounded-full" /></div>}
        {showStart && <StartCard onLater={() => update({ dismissed: true })} />}
        {srv && work.length > 0 && <WorkCard items={work} />}
        {srv && !showStart && (priorities.length > 0 ? <RecoCard priorities={priorities} /> : !st.diagLater && <GoFurther onLater={() => update({ diagLater: true })} />)}
        {srv && !showStart && !work.length && (priorities.length > 0 || st.diagLater) && <NextCard />}
        {srv && !showStart && work.length > 0 && (
          <button onClick={() => nav("/app/start?edit=1")} data-testid="home-start-link"
            className="w-full flex items-center gap-2 min-h-[48px] px-4 rounded-xl text-sm text-neon hover:bg-white/5 transition-colors">
            <Compass size={16} /> {s("start_link")}
          </button>
        )}
        {st.answers?.time && (
          <div className="flex items-center justify-between gap-2 px-4 text-sm text-[hsl(var(--muted-foreground))]" data-testid="home-time-row">
            <span className="inline-flex items-center gap-2"><Clock size={15} /> {s("time_row", { t: s(`time_${st.answers.time}`).toLowerCase() })}</span>
            <button onClick={() => nav("/app/start?edit=1")} data-testid="home-time-edit" className="text-neon min-h-[44px] px-2">{s("modify")}</button>
          </div>
        )}
        {srv && <ToolsAccordion />}
      </div>
    </Layout>
  );
}
